<template>
  <HoverCard v-if="collapsed" :open-delay="100" :close-delay="100">
    <HoverCardTrigger as-child>
      <button
        type="button"
        :class="[
          'flex items-center justify-center rounded-lg p-2 h-9 w-9 mx-auto my-0.5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          containsActive()
            ? 'bg-muted text-foreground'
            : 'text-foreground/80 hover:bg-muted hover:text-foreground',
        ]"
      >
        <component :is="headerIconComponent" v-if="headerIconComponent" class="h-4 w-4 shrink-0" />
      </button>
    </HoverCardTrigger>
    <HoverCardContent side="right" align="start" :side-offset="8" class="w-56 p-1">
      <p class="px-2 py-1.5 text-xs font-medium text-muted-foreground">{{ title }}</p>
      <NavLink
        v-for="(link, i) in links"
        :key="i"
        :text="link.text"
        :icon="link.icon"
        :link="link.route"
        :sibling-routes="linkRoutes"
      />
    </HoverCardContent>
  </HoverCard>
  <Collapsible v-else v-model:open="open" class="px-2">
    <CollapsibleTrigger
      class="w-full flex items-center justify-between gap-2 rounded-lg p-2 text-sm font-medium text-foreground/90 hover:bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <span class="flex items-center gap-2">
        <component :is="headerIconComponent" v-if="headerIconComponent" class="h-4 w-4 shrink-0" />
        <span>{{ title }}</span>
      </span>
      <ChevronDown :class="['h-4 w-4 transition-transform', open ? 'rotate-180' : '']" />
    </CollapsibleTrigger>
    <CollapsibleContent class="pl-2 mt-1 space-y-0.5">
      <NavLink
        v-for="(link, i) in links"
        :key="i"
        :text="link.text"
        :icon="link.icon"
        :link="link.route"
        :sibling-routes="linkRoutes"
      />
    </CollapsibleContent>
  </Collapsible>
</template>

<script>
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../collapsible/index.js'
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../hover-card/index.js'
import { ChevronDown } from 'lucide-vue-next'
import * as LucideIcons from 'lucide-vue-next'
import NavLink from './NavLink.vue'

export default {
  name: 'SidebarGroup',
  components: { Collapsible, CollapsibleTrigger, CollapsibleContent, ChevronDown, NavLink, HoverCard, HoverCardTrigger, HoverCardContent },
  props: {
    title: { type: String, required: true },
    icon: { type: String, default: null },
    prefix: { type: String, default: null },
    links: { type: Array, default: () => [] },
    // Collapsed (icon-only) presentation, controlled by the parent shell.
    collapsed: { type: Boolean, default: false },
  },
  data() {
    return {
      open: this.containsActive(),
    }
  },
  computed: {
    headerIconComponent() {
      if (!this.icon) return null
      return LucideIcons[this.icon] || null
    },
    /** The routes of this group's links, passed down so NavLink can resolve the deepest match. */
    linkRoutes() {
      return (this.links || []).map((l) => l.route).filter(Boolean)
    },
  },
  watch: {
    '$route.path'() {
      if (this.containsActive()) this.open = true
    },
  },
  methods: {
    containsActive() {
      // Expand when the current URL falls under this group's prefix (e.g. /cashback-program/*)…
      if (this.prefix && (this.$route.path === this.prefix || this.$route.path.startsWith(this.prefix + '/'))) {
        return true
      }
      // …or matches one of the group's link routes directly.
      return (this.links || []).some(
        (l) => this.$route.path === l.route || this.$route.path.startsWith(l.route + '/')
      )
    },
  },
}
</script>
